import React from 'react';
import { Milestone } from '@/features/projects/types';

interface MilestoneProgressProps {
    milestones: Milestone[];
    referralsCount: number;
    primaryColor: string;
}

export function MilestoneProgress({ milestones, referralsCount, primaryColor }: MilestoneProgressProps) {
    if (!milestones || milestones.length === 0) return null;

    // Sort by referrals_required ascending
    const sorted = [...milestones].sort((a, b) => a.referrals_required - b.referrals_required);

    // Find the next incomplete milestone
    const nextMilestone = sorted.find((m) => referralsCount < m.referrals_required);
    const maxRequired = sorted[sorted.length - 1].referrals_required;

    // Progress as % toward the next milestone
    const progressTo = nextMilestone ? nextMilestone.referrals_required : maxRequired;
    const previousRequired = nextMilestone
        ? (sorted[sorted.indexOf(nextMilestone) - 1]?.referrals_required ?? 0)
        : sorted[sorted.length - 2]?.referrals_required ?? 0;

    const rangeSize = progressTo - previousRequired;
    const progressInRange = Math.min(referralsCount - previousRequired, rangeSize);
    const progressPercent = rangeSize > 0 ? Math.max(0, (progressInRange / rangeSize) * 100) : 100;

    return (
        <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm font-semibold text-gray-800 mb-4 text-center">
                Referral Rewards
            </p>

            {/* Milestone list */}
            <div className="space-y-3 mb-5">
                {sorted.map((milestone) => {
                    const isCompleted = referralsCount >= milestone.referrals_required;
                    const isNext = milestone === nextMilestone;
                    return (
                        <div
                            key={milestone.id}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${isCompleted
                                    ? 'bg-green-50 border-green-200'
                                    : isNext
                                        ? 'border-2 bg-white shadow-sm'
                                        : 'bg-gray-50 border-gray-200'
                                }`}
                            style={isNext ? { borderColor: primaryColor } : undefined}
                        >
                            {/* Status dot */}
                            <div
                                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${isCompleted
                                        ? 'bg-green-500 text-white'
                                        : isNext
                                            ? 'text-white'
                                            : 'bg-gray-200 text-gray-400'
                                    }`}
                                style={isNext ? { backgroundColor: primaryColor } : undefined}
                            >
                                {isCompleted ? (
                                    <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M13 4L6.5 11 3 7.5" />
                                    </svg>
                                ) : (
                                    milestone.referrals_required
                                )}
                            </div>

                            {/* Reward info */}
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold truncate ${isCompleted ? 'text-green-700' : 'text-gray-900'}`}>
                                    {milestone.reward_name}
                                </p>
                                <p className={`text-xs ${isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                                    {isCompleted
                                        ? 'Unlocked!'
                                        : `${milestone.referrals_required} referral${milestone.referrals_required !== 1 ? 's' : ''} required`}
                                </p>
                            </div>

                            {/* Lock icon for future milestones */}
                            {!isCompleted && !isNext && (
                                <svg className="w-4 h-4 text-gray-300 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Progress bar toward next milestone */}
            {nextMilestone && (
                <div>
                    <div className="flex justify-between items-center mb-1.5">
                        <span className="text-xs text-gray-500">Progress to next reward</span>
                        <span className="text-xs font-semibold text-gray-700">
                            {referralsCount} / {nextMilestone.referrals_required}
                        </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                            className="h-2 rounded-full transition-all duration-700 ease-out"
                            style={{
                                width: `${progressPercent}%`,
                                backgroundColor: primaryColor,
                            }}
                        />
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-500">
                        <span className="font-semibold text-gray-700" style={{ color: primaryColor }}>
                            {nextMilestone.referrals_required - referralsCount}
                        </span>{' '}
                        more referral{nextMilestone.referrals_required - referralsCount !== 1 ? 's' : ''} to unlock{' '}
                        <span className="font-semibold text-gray-700">{nextMilestone.reward_name}</span>
                    </p>
                </div>
            )}

            {!nextMilestone && (
                <div className="text-center py-2">
                    <span className="text-sm font-semibold text-green-600">
                        🎉 All rewards unlocked!
                    </span>
                </div>
            )}
        </div>
    );
}
